import type { LinkProps } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { cn, PAGE_TITLE_CLASS, Tooltip } from '@/components/base'
import { AssumeRoleMenu } from '@/components/Internal/AssumeRoleMenu'
import { NotificationBell } from '@/components/Internal/NotificationBell'
import { Tabs, type Tab } from '@/components/Internal/Tabs'
import { ThemeSwitch } from '@/components/Internal/ThemeSwitch'
import { LinkButton } from '@/components/LinkButton'
import { UserMenu } from '@/components/UserMenu'

type PageLayoutProps = {
  title: string
  /** Where the arrow to the left of the title leads. */
  backTo?: LinkProps['to']
  tabs?: Tab[]
  activeTab?: string
  /** For tabs that switch a view within one route; a tab with `to` navigates. */
  onTabChange?: (id: string) => void
  /** Ground and padding for the body, where the page wants more than a bare frame. */
  contentClassName?: string
  children?: ReactNode
}

/**
 * The frame every Internal screen wears: one bar carrying the title, the
 * page's tabs and everything global, and a body under it.
 *
 * A screen's own buttons do not belong here: they go at the end of its filter
 * row, beside the rows they act on, which is what `DataTable`'s `toolbarExtra`
 * is for.
 *
 * Tabs sit in the bar rather than on a row of their own — a second full-width
 * strip cost 44 px of every screen to draw three words.
 */
export function PageLayout({
  title,
  backTo,
  tabs,
  activeTab,
  onTabChange,
  contentClassName,
  children,
}: PageLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card">
      <header className="flex h-13 shrink-0 flex-row items-center gap-3.5 border-b border-line-soft px-[18px]">
        {backTo ? (
          <Tooltip label={COMMON.prev}>
            <LinkButton
              to={backTo}
              variant="ghost"
              size="icon"
              aria-label={COMMON.prev}
            >
              <ArrowLeft size={16} />
            </LinkButton>
          </Tooltip>
        ) : null}

        <h1 className={cn('shrink-0', PAGE_TITLE_CLASS)}>{title}</h1>

        {tabs ? (
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={onTabChange}
            className="min-w-0 overflow-x-auto"
          />
        ) : null}

        <div className="ml-auto flex shrink-0 flex-row items-center gap-2">
          <AssumeRoleMenu />
          <NotificationBell />
          <ThemeSwitch />
          <UserMenu />
        </div>
      </header>

      <div className={cn('flex min-h-0 flex-1 flex-col', contentClassName)}>
        {children}
      </div>
    </div>
  )
}
